import React, { useEffect, useRef, useState } from "react";
import { FieldTemplateDataType, TemplateDataType } from "../../../types";
import { useGetIdentity, useTranslation } from "@refinedev/core";
import dayjs from "dayjs";
import ApiService from "utills/ApiService";
import toast from "react-hot-toast";
import { IUserData } from "interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { COUNTRY } from "../../utils/country";
import { get } from "lodash";
import useWindowWidth from "utills/InitWidthDevice";
import { CalendarIcon } from "@heroicons/react/outline";
import { calculateAge } from "utills/helpers";
import moment from "moment";
import { REGEX_VALID_EMAIL } from "utills/constants";
import { CollapsibleSection } from "components/collapsibleSection";
import Button from "components/button";
import { useScrollToId } from "../../hooks/useScrollToId";
import Input from "components/input";
import Select from "components/select";
import DateTimePicker from "components/datetimePicker";

import "./styles.scss";
import { Label } from "components/label";

interface PropType {
  initData: Record<string, TemplateDataType> | null; // 3 key: { participant, registration , template_data }
}

const AttendeesForm = (props: PropType) => {
  const { initData } = props;

  const params = useParams();
  const navigate = useNavigate();
  const { translate, getLocale } = useTranslation();
  const { data: user } = useGetIdentity<IUserData>();
  const language = getLocale();
  const windowWidth = useWindowWidth();
  const scrollToId = useScrollToId();

  const [formData, setFormData] = useState<Record<string, any>>(
    initData?.registration || {}
  );
  // const [formData, setFormData] = useState<Record<string, any>>({
  //   ...(initData?.registration ?? {}), // đảm bảo không bị lỗi khi undefined
  //   test_multi_select: ["multi_1"],
  // });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);

  const datePickerContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [datePickerWidths, setDatePickerWidths] = useState<number[]>([]);

  const today = dayjs().format("YYYY-MM-DD");
  // const participantBirthday =
  //   initData?.registration?.dob ?? initData?.registration?.birthday ?? today;

  const checkIsParticipantAdult = (date: string): boolean => {
    const birthDate = dayjs(date);
    const today = dayjs();

    return today.diff(birthDate, "year") >= 18;
  };

  const [isAdult, setIsAdult] = useState<boolean>(false);

  // const isAdult = checkIsParticipantAdult(participantBirthday);

  // Update isAdult value when user change DOB input field
  useEffect(() => {
    if (formData?.dob) {
      const isAdultValue = checkIsParticipantAdult(formData?.dob);

      setIsAdult(isAdultValue);
    }
  }, [formData?.dob]);

  // ========== Input case
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ========== Select case
  const formatSelectOptions = (
    optionObj: Record<string, Record<string, string>>,
    langKey: string // langKey example: label_vi
  ) => {
    return Object.entries(optionObj).map(([value, labels]) => ({
      value,
      label: labels[langKey],
    }));
  };

  const getSelectOptionByValue = (
    value: string,
    options: Record<string, Record<string, string>>,
    langKey: string
  ) => {
    const label = options[value]?.[langKey];
    if (!label) return null;

    return {
      value,
      label,
    };
  };

  const handleChangeSelectOption = (
    name: string,
    option: { value: string; label: string }
  ) => {
    // const { value, label } = option;
    const value = option?.value;

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: value ?? "",
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const getAreaCodeSelectOption = (
    code: string,
    country: { label: string; value: string; code: string }[]
  ) => {
    const findCountry = country.find((c) => c.code === code);

    if (!findCountry) return null;

    return {
      value: findCountry.code,
      label: `${findCountry.label} | +${findCountry.code}`,
    };
  };

  const getIsAreaCodeError = (key: string) => {
    const error = errors[key];

    if (error === "" || error === undefined) return false;

    return true;
  };

  // ========== Radio case
  const renderRadioGroup = (
    name: string,
    options: {
      key: string;
      label: Record<string, string>;
    }[],
    selected: string,
    onChange: (value: string) => void,
    language: string
  ) => {
    return (
      <div className="radio-button flex flex-col gap-3">
        {options.map((option) => (
          <label key={option.key} className="mr-4 flex items-center gap-3">
            <input
              type="radio"
              name={name}
              value={option.key}
              checked={selected === option.key}
              onChange={() => onChange(option.key)}
              className="mr-1 focus:outline-none focus:ring-0"
            />
            {option.label[language] ?? option.key}
          </label>
        ))}
      </div>
    );
  };

  const handleChangeRadio = (name: string, value: string) => {
    console.log("Radio change:", { name, value });

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ========== Checkbox case
  const handleGetCheckBoxValue = (value: string | boolean | number) => {
    if (value === "0") return false;
    if (value === "1") return true;
    if (value === true) return true;
    if (value === false) return false;
    if (value === 1) return true;
    if (value === 0) return false;

    return false;
  };

  const handleChangeCheckBox = (
    name: string,
    value: string | boolean | number
  ) => {
    console.log("Checkbox change:", { name, value });

    // Set form data value
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        [name]: "1",
      }));
    } else if (value === "0" || value === "1") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "0" ? "1" : "0",
      }));
    } else if (value === 0 || value === 1) {
      setFormData((prev) => ({
        ...prev,
        [name]: value === 0 ? 1 : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: !value,
      }));
    }

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ========== Multiselect case
  const formatMultiSelectOptions = (
    data: { key: string; label: Record<string, string> }[],
    langKey: string // langKey example: vi
  ) => {
    return data.map((item) => ({
      value: item.key,
      label: item.label[langKey],
    }));
  };

  // const handleMultiChangeSelectOption = (
  //   name: string,
  //   options: { label: string; value: string }[]
  // ) => {
  //   console.log("Multi select change:", { name, options });

  //   const formatOptions = options.map((o) => {
  //     return o.value;
  //   });

  //   // Set form data value
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: formatOptions,
  //   }));

  //   // Reset error log
  //   setErrors((prev) => ({
  //     ...prev,
  //     [name]: "",
  //   }));
  // };

  const handleMultiChangeSelectOption = (
    name: string,
    options: { label: string; value: string }[]
  ) => {
    console.log("Multi select change:", { name, options });

    const formattedValue = options.map((o) => o.value).join(","); // chuyển thành chuỗi cách nhau dấu phẩy

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // const getMultiSelectOptionByValue = (
  //   values: string[],
  //   options: { key: string; label: Record<string, string> }[],
  //   langKey: string // langKey example: vi
  // ) => {
  //   return options
  //     .filter((opt) => values.includes(opt.key))
  //     .map((opt) => ({
  //       value: opt.key,
  //       label: opt.label[langKey],
  //     }));
  // };

  const getMultiSelectOptionByValue = (
    values: string, // example: "multi_1,multi_2"
    options: { key: string; label: Record<string, string> }[],
    langKey: string
  ) => {
    if (typeof values !== "string") return [];

    const valueArray = values
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    return options
      .filter((opt) => valueArray.includes(opt.key))
      .map((opt) => ({
        value: opt.key,
        label: opt.label[langKey],
      }));
  };

  // ========== Datetime case
  const formatDatetime = (dateString: string): Date | null => {
    const parsed = new Date(dateString);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateToString = (date: Date | null): string => {
    if (!date) return "";
    return dayjs(date).format("MM/DD/YYYY HH:mm:ss");
  };

  const handleChangeDateTime = (name: string, value: string) => {
    console.log("Datetime change:", { name, value });

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const DatePickerInput = React.forwardRef(
    (
      {
        value,
        width,
        onClick,
      }: {
        value?: string;
        width?: number;
        onClick?: () => void;
      },
      ref: React.Ref<HTMLButtonElement>
    ) => (
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        style={{ width }}
        className="relative h-[44px] p-2 border border-gray-300 rounded text-left"
      >
        {value || "Chọn ngày"}

        <CalendarIcon className="absolute right-[10px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-black" />
      </button>
    )
  );

  // ========== Date case
  const formatDate = (value: string) => {
    return value ? dayjs(value).toDate() : null;
  };

  const handleChangeDate = (name: string, value: Date | null) => {
    let formatValue = "";

    if (value) formatValue = dayjs(value).format("YYYY-MM-DD");

    // Set form data value
    setFormData((prev) => ({
      ...prev,
      [name]: formatValue,
    }));

    // Reset error log
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleBack = () => {
    navigate(`/attendees/sports/${params?.id}/detail`);
  };

  const isValidRender = (key: string) => {
    const targetField =
      key.includes("legal") || key.includes("runner_relationship");

    if (!isAdult && targetField) return true; // hiện nếu chưa là người lớn
    if (isAdult && targetField) return false; // ẩn nếu là người lớn

    return true;
  };

  const handleValidateField = (
    newErrors: Record<string, string>,
    value: any,
    field: FieldTemplateDataType,
    langKey: string, // langKey exmaple: vi
    dob: string | null = null,
    startDate: string
  ) => {
    const key = field.key;
    const label = (field as any)[`label_${langKey}`] || "This field";

    // Xóa lỗi cũ nếu có
    delete newErrors[key];

    // Nếu là field "legal_*" hoặc "runner_relationship" → bỏ qua nếu đủ 18 tuổi
    if (
      (key.includes("legal") || key.includes("runner_relationship")) &&
      isAdult
    ) {
      console.log("Skip:", key);

      return;
    }

    let isRequired = field.require === true;

    // Nếu là field "legal_*" hoặc "runner_relationship" → bắt buộc nhập nếu chưa đủ 18 tuổi
    if (
      (key.includes("legal") || key.includes("runner_relationship")) &&
      !isAdult
    ) {
      console.log("Require:", key);

      isRequired = true;
    }

    // Validate theo loại field
    switch (field.type) {
      case "checkbox":
        const isChecked = value === "1" || value === true || value === 1;
        console.log("CHECKBOX VALUE VALIDATE:", { value, isChecked, field });

        if (isRequired && !isChecked) {
          newErrors[key] = `${translate("field-required")} ${label}`;
        }
        break;

      case "number": {
        if (isRequired && !value) {
          newErrors[key] = `${translate("field-required")} ${label}`;
          break;
        }

        if (value) {
          const num = Number(value);
          if (isNaN(num)) {
            newErrors[key] = `${label} ${translate("valid_number")}`;
            break;
          }

          const min = get(field.option, "min");
          const max = get(field.option, "max");

          // if (min !== undefined && num < min) {
          //   newErrors[key] = `${label} must be at least ${min}`;
          // } else if (max !== undefined && num > max) {
          //   newErrors[key] = `${label} must be at most ${max}`;
          // }

          if (min !== undefined && num < min) {
            newErrors[key] = translate("min_number", { label, min });
          } else if (max !== undefined && num > max) {
            newErrors[key] = translate("max_number", { label, max });
          }
        }
        break;
      }

      case "date": {
        if (isRequired && !value) {
          newErrors[key] = `${translate("field-required")} ${label}`;
          break;
        }

        if (field.validation === "dob" && value) {
          const birthDate = new Date(value);
          if (isNaN(birthDate.getTime())) {
            newErrors[key] = `${label} ${translate("valid_date")}`;
            break;
          }

          const minAge = get(field.option, "min");
          const maxAge = get(field.option, "max");
          const byDate = get(field.option, "by") || startDate;
          const age = calculateAge(birthDate, new Date(byDate));

          // if (minAge !== undefined && age < minAge) {
          //   newErrors[key] = `${label} must be at least ${minAge} years old${
          //     byDate ? ` as of ${moment(byDate).format("DD/MM/YYYY")}` : ""
          //   }`;
          // } else if (maxAge !== undefined && age > maxAge) {
          //   newErrors[key] = `${label} must be at most ${maxAge} years old${
          //     byDate ? ` as of ${moment(byDate).format("DD/MM/YYYY")}` : ""
          //   }`;
          // }

          if (minAge !== undefined && age < minAge) {
            newErrors[key] = `${label} - ${translate("valid_age_min", {
              min: minAge,
            })}`;
          } else if (
            minAge !== undefined &&
            maxAge !== undefined &&
            (age < minAge || age > maxAge)
          ) {
            newErrors[key] = `${label} - ${translate("valid_age", {
              min: minAge,
              max: maxAge,
            })}`;
          }
        }
        break;
      }

      case "text": {
        if (isRequired && !value) {
          newErrors[key] = `${translate("field-required")} ${label}`;
          break;
        }

        if (field.validation === "email" && value) {
          if (!REGEX_VALID_EMAIL.test(value)) {
            newErrors[key] = `${translate("wrong-format-email")}`;
          }
        }

        if (field.validation === "length") {
          const min = get(field.option, "min");
          const max = get(field.option, "max");

          // if (min !== undefined && value.length < min) {
          //   newErrors[key] = `${label} must be at least ${min} characters`;
          // } else if (max !== undefined && value.length > max) {
          //   newErrors[key] = `${label} must be at most ${max} characters`;
          // }

          if (min !== undefined && value?.length < min) {
            newErrors[key] = translate("min_length", { label, min });
          } else if (max !== undefined && value?.length > max) {
            newErrors[key] = translate("max_length", { label, max });
          }
        }

        break;
      }

      default: {
        if (isRequired && !value) {
          newErrors[key] = `${translate("field-required")} ${label}`;
        }
        break;
      }
    }

    // Nếu là field phone thì cần validate _area_code riêng nếu muốn
    if (key.includes("phone") && field.validation === "phone") {
      const areaKey = `${key}_area_code`;
      const areaSelectValue = formData[`${key}_area_code`];

      if (isRequired && !areaSelectValue) {
        newErrors[areaKey] = `${translate("field-required")}`;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    const data = { ...formData };

    // Validate required fields
    Object.entries(initData?.template_data || {})?.forEach(
      ([_, section]: [string, TemplateDataType]) => {
        console.log(section);

        section?.fields?.forEach((field) => {
          const value = data[field.key];

          const registration: any = initData?.registration;
          const participant: any = initData?.participant;

          const dob: string = dayjs(
            registration?.birthday || new Date()
          ).format("YYYY-MM-DD");
          const startDate: string = dayjs(
            participant?.start_date || new Date()
          ).format("YYYY-MM-DD"); // Nếu không có start_date thì lấy ngày hiện tại

          handleValidateField(
            newErrors,
            value,
            field,
            language ?? "vi",
            dob,
            startDate
          );
        });
      }
    );

    if (Object.keys(newErrors).length > 0) {
      console.log("Submit failed:", newErrors);
      const firstErrorKey = Object.keys(newErrors)[0];

      console.log("[firstErrorKey]", firstErrorKey);

      toast.error(translate("update-failed"));
      setErrors(newErrors);
      scrollToId(firstErrorKey, { offset: 250 });

      return;
    }

    try {
      if (!params?.id || !user?.token) {
        return;
      }

      setLoading(true);

      const submitData = {
        participant_data: { ...data },
      };

      const result = await ApiService.put(
        `participants/${params?.id}`,
        submitData,
        user?.token
      );

      console.log("Submit thành công:", submitData, result);
      toast.success(translate("success"));

      // Navigate to detail page when edit success
      const intervalRedirect = setInterval(() => {
        navigate(`/attendees/sports/${params?.id}/detail`);

        clearInterval(intervalRedirect);
      }, 3000);
    } catch (error: any) {
      console.log("Update failed:", error);

      toast.error(error?.message);
    } finally {
      setLoading(false);
      setErrors({});
    }
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  // Get Datetime input width
  useEffect(() => {
    const widths = datePickerContainerRefs.current.map((el) =>
      el ? el.offsetWidth : 0
    );

    setDatePickerWidths(widths);
  }, [initData?.template_data, isMobile]);

  console.log("INIT DATA:", initData);
  console.log("FORM DATA:", formData); // Get value form initData.registration
  console.log("isAdult:", isAdult);

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <CollapsibleSection
        defaultOpen={true}
        label={translate("registration-information")}
      >
        <div className="p-6">
          {Object.entries(initData?.template_data || {}).map(
            ([key, value]: [string, TemplateDataType], index, arr) => {
              const isLast = index === arr.length - 1;

              return (
                <div key={key} className="mb-[20px] flex flex-col gap-5">
                  <h3 className="my-0 text-[0.95rem] font-bold text-gray-600">
                    {get(value, `label_${language}`, "")}
                  </h3>

                  {/* Form field render */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {value?.fields?.map((field, index) => {
                      const fieldValue = formData[field?.key] || "";

                      // React datepicker width
                      const datePickerCtnWidth = datePickerWidths[index] || 200;

                      return (
                        <>
                          {!isValidRender(field?.key) ? null : (
                            <div
                              key={field?.key}
                              className="w-full flex flex-col gap-1"
                            >
                              <label className="flex items-center gap-2 mb-1 text-sm font-medium">
                                {get(field, `label_${language}`, "")}

                                {field?.require && (
                                  <img
                                    src="/images/icons/icon-require.svg"
                                    alt="icon-require"
                                    className="icon-require"
                                  />
                                )}
                              </label>

                              {/* Select type */}
                              {field?.type === "select" && (
                                // <Select
                                //   isClearable={fieldValue}
                                //   styles={{
                                //     control: (provided: any, state: any) => ({
                                //       ...provided,
                                //       height: "44px",
                                //       borderColor: errors[field?.key]
                                //         ? "#ED4927"
                                //         : "#ccc",
                                //     }),
                                //     input: (base) => ({
                                //       ...base,
                                //       "input:focus": {
                                //         boxShadow: "none",
                                //       },
                                //     }),
                                //   }}
                                //   options={formatSelectOptions(
                                //     field?.option,
                                //     `label_${language}`
                                //   )}
                                //   value={
                                //     getSelectOptionByValue(
                                //       fieldValue,
                                //       field?.option,
                                //       `label_${language}`
                                //     ) ?? { value: "", label: "" }
                                //   }
                                //   onChange={(option) => {
                                //     handleChangeSelectOption(
                                //       field?.key,
                                //       option as {
                                //         value: string;
                                //         label: string;
                                //       }
                                //     );
                                //   }}
                                //   placeholder="Chọn"
                                // />

                                <Select
                                  id={field?.key}
                                  isClearable={fieldValue}
                                  error={errors[field?.key]}
                                  options={formatSelectOptions(
                                    field?.option,
                                    `label_${language}`
                                  )}
                                  className="py-0.5"
                                  value={
                                    getSelectOptionByValue(
                                      fieldValue,
                                      field?.option,
                                      `label_${language}`
                                    ) ?? null
                                  }
                                  onChange={(selected: {
                                    label: string;
                                    value: string;
                                  }) => {
                                    handleChangeSelectOption(
                                      field?.key,
                                      selected as {
                                        value: string;
                                        label: string;
                                      }
                                    );
                                  }}
                                />
                              )}

                              {/* Text type */}
                              {field?.type === "text" &&
                                field?.validation !== "phone" && (
                                  <input
                                    id={field?.key}
                                    name={field?.key}
                                    type="text"
                                    value={fieldValue}
                                    onChange={handleChange}
                                    className={`w-full h-[44px] text-[0.875rem] border ${
                                      errors[field?.key]
                                        ? "border-red-600 focus:border-red-600"
                                        : "border-gray-300 focus:border-brand-500"
                                    } rounded-lg p-2 focus:outline-none focus:ring-0`}
                                  />
                                )}

                              {/* Phone */}
                              {field?.validation === "phone" && (
                                <div className="flex items-center gap-2">
                                  {/* <Select
                                    styles={{
                                      control: (
                                        provided: any,
                                        state: any
                                      ) => ({
                                        ...provided,
                                        width: "250px",
                                        height: "44px",
                                        borderColor: errors[
                                          `${field?.key}_area_code`
                                        ]
                                          ? "#ED4927"
                                          : "#ccc",
                                      }),
                                      input: (base) => ({
                                        ...base,
                                        "input:focus": {
                                          boxShadow: "none",
                                        },
                                      }),
                                    }}
                                    options={COUNTRY.map((c) => {
                                      return {
                                        value: c.code,
                                        label: `${c.label} | +${c.code}`,
                                      };
                                    })}
                                    value={getAreaCodeSelectOption(
                                      formData[`${field?.key}_area_code`],
                                      COUNTRY
                                    )}
                                    onChange={(option) => {
                                      handleChangeSelectOption(
                                        `${field?.key}_area_code`,
                                        option as {
                                          value: string;
                                          label: string;
                                        }
                                      );
                                    }}
                                    placeholder="Chọn"
                                    isClearable
                                  /> */}

                                  <Select
                                    id={`${field?.key}_area_code`}
                                    error={getIsAreaCodeError(
                                      `${field?.key}_area_code`
                                    )}
                                    isClearable
                                    options={COUNTRY.map((c) => {
                                      return {
                                        value: c.code,
                                        label: `${c.label} | +${c.code}`,
                                      };
                                    })}
                                    className="py-0.5 w-[150px] md:w-[250px]"
                                    value={getAreaCodeSelectOption(
                                      formData[`${field?.key}_area_code`],
                                      COUNTRY
                                    )}
                                    onChange={(selected: {
                                      label: string;
                                      value: string;
                                    }) => {
                                      handleChangeSelectOption(
                                        `${field?.key}_area_code`,
                                        selected as {
                                          value: string;
                                          label: string;
                                        }
                                      );
                                    }}
                                  />

                                  <input
                                    id={field?.key}
                                    name={field?.key}
                                    type="text"
                                    value={fieldValue}
                                    onChange={handleChange}
                                    className={`w-full h-[44px] text-[0.875rem] border ${
                                      errors[field?.key]
                                        ? "border-red-600 focus:border-red-600"
                                        : "border-gray-300 focus:border-brand-500"
                                    } rounded-lg p-2 focus:outline-none focus:ring-0`}
                                  />
                                </div>
                              )}

                              {/* Number type */}
                              {field?.type === "number" && (
                                <input
                                  id={field?.key}
                                  name={field?.key}
                                  type="number"
                                  value={fieldValue}
                                  onChange={handleChange}
                                  className={`w-full h-[44px] text-[0.875rem] border ${
                                    errors[field?.key]
                                      ? "border-red-600 focus:border-red-600"
                                      : "border-gray-300 focus:border-brand-500"
                                  } rounded-lg p-2 focus:outline-none focus:ring-0`}
                                />
                              )}

                              {/* Date type */}
                              {field?.type === "date" && (
                                <input
                                  id={field?.key}
                                  name={field?.key}
                                  type="date"
                                  value={fieldValue}
                                  onChange={(e) => {
                                    console.log("VALUE:", e.target.value);

                                    handleChange(e);
                                  }}
                                  className={`w-full h-[44px] text-[0.875rem] border ${
                                    errors[field?.key]
                                      ? "border-red-600 focus:border-red-600"
                                      : "border-gray-300 focus:border-brand-500"
                                  } rounded-lg p-2 focus:outline-none focus:ring-0`}
                                />

                                // <div className="attendees-datepicker w-full flex relative">
                                //   <ReactDatePicker
                                //     locale={language ?? "vi"}
                                //     showMonthDropdown // dropdown chọn tháng
                                //     showYearDropdown // dropdown chọn năm
                                //     dropdownMode="select" // hiển thị dropdown dạng select
                                //     selected={formatDate(fieldValue)}
                                //     onChange={(value) => {
                                //       handleChangeDate(field?.key, value);
                                //     }}
                                //     dateFormat="dd/MM/yyyy"
                                //     placeholderText="Chọn ngày"
                                //     className={`w-full h-[44px] border ${
                                //       errors[field?.key]
                                //         ? "border-red-500"
                                //         : "border-gray-300"
                                //     } rounded p-2`}
                                //     onChangeRaw={(e) => {
                                //       const rawValue = e?.target?.value;

                                //       if (typeof rawValue !== "string")
                                //         return;

                                //       let val = rawValue.replace(/\D/g, ""); // chỉ lấy số

                                //       // Giới hạn tối đa 8 ký tự (ddmmYYYY)
                                //       val = val.slice(0, 8);

                                //       if (
                                //         val.length >= 2 &&
                                //         val.length <= 4
                                //       ) {
                                //         e.target.value = val.replace(
                                //           /(\d{2})(\d{1,2})/,
                                //           "$1/$2"
                                //         );
                                //       } else if (val.length > 4) {
                                //         e.target.value = val.replace(
                                //           /(\d{2})(\d{2})(\d{1,4})/,
                                //           "$1/$2/$3"
                                //         );
                                //       } else {
                                //         e.target.value = val;
                                //       }
                                //     }}
                                //   />

                                //   <CalendarIcon className="absolute right-[10px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-black" />
                                // </div>
                              )}

                              {/* Radio_button type */}
                              {field?.type === "radio_button" && (
                                <>
                                  {renderRadioGroup(
                                    field?.key,
                                    field?.option,
                                    fieldValue,
                                    (value) => {
                                      handleChangeRadio(field?.key, value);
                                    },
                                    language ?? "vi"
                                  )}
                                </>
                              )}

                              {/* Checkbox type */}
                              {field?.type === "checkbox" && (
                                // <input
                                //   className="focus:outline-none focus:ring-0"
                                //   type="checkbox"
                                //   checked={handleGetCheckBoxValue(fieldValue)}
                                //   onChange={() => {
                                //     handleChangeCheckBox(
                                //       field?.key,
                                //       fieldValue
                                //     );
                                //   }}
                                // />

                                <Input
                                  id={field?.key}
                                  error={errors[field?.key]}
                                  className="px-2 py-2"
                                  type="checkbox"
                                  checked={handleGetCheckBoxValue(fieldValue)}
                                  onChange={() => {
                                    handleChangeCheckBox(
                                      field?.key,
                                      fieldValue
                                    );
                                  }}
                                />
                              )}

                              {/* Multiselect type */}
                              {field?.type === "multi_select" && (
                                // <Select
                                //   isMulti
                                //   styles={{
                                //     control: (provided: any, state: any) => ({
                                //       ...provided,
                                //       height: "44px",
                                //       borderColor: errors[field?.key]
                                //         ? "#ED4927"
                                //         : "#ccc",
                                //     }),
                                //     input: (base) => ({
                                //       ...base,
                                //       "input:focus": {
                                //         boxShadow: "none",
                                //       },
                                //     }),
                                //   }}
                                //   options={formatMultiSelectOptions(
                                //     field?.option,
                                //     `${language}`
                                //   )}
                                //   value={
                                //     getMultiSelectOptionByValue(
                                //       fieldValue,
                                //       field?.option,
                                //       `${language}`
                                //     ) ?? []
                                //   }
                                //   onChange={(options) => {
                                //     handleMultiChangeSelectOption(
                                //       field?.key,
                                //       options as {
                                //         label: string;
                                //         value: string;
                                //       }[]
                                //     );
                                //   }}
                                //   placeholder="Chọn"
                                // />

                                <Select
                                  isMulti
                                  error={errors[field?.key]}
                                  id={field?.key}
                                  options={formatMultiSelectOptions(
                                    field?.option,
                                    `${language}`
                                  )}
                                  className="py-0.5"
                                  value={
                                    getMultiSelectOptionByValue(
                                      fieldValue,
                                      field?.option,
                                      `${language}`
                                    ) ?? []
                                  }
                                  onChange={(
                                    selected: { label: string; value: string }[]
                                  ) => {
                                    handleMultiChangeSelectOption(
                                      field?.key,
                                      selected as {
                                        label: string;
                                        value: string;
                                      }[]
                                    );
                                  }}
                                />
                              )}

                              {/* Datetime type */}
                              {field?.type === "datetime" && (
                                // <div
                                //   id={field?.key}
                                //   ref={(el) =>
                                //     (datePickerContainerRefs.current[index] =
                                //       el)
                                //   }
                                //   className="w-full"
                                // >
                                //   <ReactDatePicker
                                //     showTimeSelect
                                //     placeholderText="Chọn ngày"
                                //     dateFormat="dd/MM/yyyy HH:mm:ss"
                                //     selected={formatDatetime(fieldValue)}
                                //     onChange={(date) => {
                                //       handleChangeDateTime(
                                //         field?.key,
                                //         formatDateToString(date)
                                //       );
                                //     }}
                                //     customInput={
                                //       <DatePickerInput
                                //         width={datePickerCtnWidth}
                                //       />
                                //     }
                                //   />
                                // </div>

                                <div id={field?.key}>
                                  <DateTimePicker
                                    error={errors[field?.key]}
                                    value={formatDatetime(fieldValue)}
                                    onChange={(date: any) => {
                                      handleChangeDateTime(
                                        field?.key,
                                        formatDateToString(date)
                                      );
                                    }}
                                  />
                                </div>
                              )}

                              {/* Sublabel */}
                              {get(field, `sub_label_${language}`, "") !==
                                "" && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {get(field, `sub_label_${language}`, "")}
                                </p>
                              )}

                              {/* Error */}
                              <div className="flex items-center gap-2">
                                {errors[`${field?.key}_area_code`] && (
                                  <p className="text-[#ED4927] w-[150px] md:w-[250px] text-[14px]">
                                    {errors[`${field?.key}_area_code`]}
                                  </p>
                                )}

                                {errors[field?.key] && (
                                  <p className="text-[#ED4927] text-[14px]">
                                    {errors[field?.key]}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })}
                  </div>

                  {!isLast && (
                    <div className="w-full h-[1px] bg-gray-200"></div>
                  )}
                </div>
              );
            }
          )}
        </div>
      </CollapsibleSection>

      <div className="fixed bottom-0 left-0 right-0 bg-white p-[16px] flex gap-3 items-center justify-end">
        <Button
          onClick={handleBack}
          className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
        >
          {translate("btn-cancel")}
        </Button>

        <Button className="w-20" type="submit" loading={loading}>
          {translate("btn-save")}
        </Button>
      </div>
    </form>
  );
};

export default AttendeesForm;
